import React, { useEffect, useState } from 'react';
import {
    Container,
    Grid,
    Paper,
    Typography,
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Chip,
    Checkbox,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import { Add, Publish, Unpublished, PlaylistAdd, Refresh } from '@mui/icons-material';
import axios from 'axios';

const TestManager = () => {
    const [tests, setTests] = useState([]);
    const [mcqs, setMcqs] = useState([]);
    const [selectedTest, setSelectedTest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [addMcqsOpen, setAddMcqsOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [working, setWorking] = useState(false);
    const [newTest, setNewTest] = useState({ name: '', description: '', durationMinutes: 30, instructions: '' });
    const [selectedMcqIds, setSelectedMcqIds] = useState(new Set());
    const [filterCategory, setFilterCategory] = useState('');
    const [filterDifficulty, setFilterDifficulty] = useState('');

    useEffect(() => {
        fetchAll();
    }, []);

    const fetchAll = async () => {
        setLoading(true);
        setError('');
        try {
            const [testsRes, mcqsRes] = await Promise.all([
                axios.get('/api/tests'), // admin gets all
                axios.get('/api/mcqs')
            ]);
            setTests(testsRes.data.tests || []);
            setMcqs(mcqsRes.data.mcqs || []);
        } catch (e) {
            setError('Failed to load tests or MCQs');
        } finally {
            setLoading(false);
        }
    };

    const openCreate = () => {
        setNewTest({ name: '', description: '' });
        setCreateOpen(true);
    };

    const createTest = async () => {
        if (!newTest.name.trim()) {
            setError('Test name is required');
            return;
        }
        if (!newTest.durationMinutes || newTest.durationMinutes < 1) {
            setError('Please provide test duration (minutes)');
            return;
        }
        setCreating(true);
        setError('');
        try {
            const { data } = await axios.post('/api/tests', newTest);
            setTests(prev => [data.test, ...prev]);
            setSelectedTest(data.test);
            setCreateOpen(false);
        } catch (e) {
            setError(e.response?.data?.error || 'Failed to create test');
        } finally {
            setCreating(false);
        }
    };

    const refreshTestMcqs = async (test) => {
        try {
            const { data } = await axios.get(`/api/tests/${test.id}/mcqs`);
            return data.mcqs || [];
        } catch {
            return [];
        }
    };

    const togglePublish = async (test, published) => {
        setWorking(true);
        setError('');
        try {
            const { data } = await axios.patch(`/api/tests/${test.id}/publish`, { published });
            setTests(prev => prev.map(t => (t.id === test.id ? data.test : t)));
            setSelectedTest(prev => (prev && prev.id === test.id ? data.test : prev));
        } catch (e) {
            setError('Failed to update publish state');
        } finally {
            setWorking(false);
        }
    };

    const openAddMcqs = (test) => {
        setSelectedTest(test);
        setSelectedMcqIds(new Set());
        setFilterCategory('');
        setFilterDifficulty('');
        setAddMcqsOpen(true);
    };

    const handleToggleSelectMcq = (id) => {
        setSelectedMcqIds(prev => {
            const copy = new Set(prev);
            if (copy.has(id)) copy.delete(id);
            else copy.add(id);
            return copy;
        });
    };

    const addSelectedMcqs = async () => {
        if (!selectedTest || selectedMcqIds.size === 0) {
            setAddMcqsOpen(false);
            return;
        }
        setWorking(true);
        try {
            const ids = Array.from(selectedMcqIds);
            // Add sequentially to preserve order as position
            for (let index = 0; index < ids.length; index++) {
                const mcqId = ids[index];
                await axios.post(`/api/tests/${selectedTest.id}/mcqs`, { mcqId, position: index + 1 });
            }
            setAddMcqsOpen(false);
        } catch (e) {
            setError('Failed to add MCQs to test');
        } finally {
            setWorking(false);
        }
    };

    const removeMcqFromTest = async (mcqId) => {
        if (!selectedTest) return;
        setWorking(true);
        try {
            await axios.delete(`/api/tests/${selectedTest.id}/mcqs/${mcqId}`);
            // Force refresh of selected test MCQs displayed list
            const mcqsInTest = await refreshTestMcqs(selectedTest);
            setSelectedTest(prev => ({ ...prev, mcqs: mcqsInTest }));
        } catch (e) {
            setError('Failed to remove MCQ');
        } finally {
            setWorking(false);
        }
    };

    const filteredMcqs = mcqs.filter(m => (
        (!filterCategory || m.category === filterCategory) &&
        (!filterDifficulty || m.difficulty === filterDifficulty)
    ));

    useEffect(() => {
        (async () => {
            if (selectedTest && selectedTest.id) {
                const mcqsInTest = await refreshTestMcqs(selectedTest);
                setSelectedTest(prev => ({ ...prev, mcqs: mcqsInTest }));
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTest?.id]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Test Manager
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                    Create tests, add MCQs, and publish them for students.
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} md={5}>
                    <Paper sx={{ p: 3 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">Tests</Typography>
                            <Box>
                                <Button startIcon={<Refresh />} onClick={fetchAll} sx={{ mr: 1 }}>
                                    Refresh
                                </Button>
                                <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
                                    New Test
                                </Button>
                            </Box>
                        </Box>
                        <List>
                            {tests.map((test, index) => (
                                <ListItem
                                    key={test.id}
                                    divider={index < tests.length - 1}
                                    button
                                    selected={selectedTest?.id === test.id}
                                    onClick={() => setSelectedTest(test)}
                                >
                                    <ListItemText
                                        primary={
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Typography variant="subtitle1">{test.name}</Typography>
                                                <Chip
                                                    size="small"
                                                    label={test.published ? 'Published' : 'Draft'}
                                                    color={test.published ? 'success' : 'default'}
                                                />
                                            </Box>
                                        }
                                        secondary={test.description || 'No description'}
                                    />
                                    <ListItemSecondaryAction>
                                        <Button
                                            variant="contained"
                                            color={test.published ? 'warning' : 'success'}
                                            onClick={() => togglePublish(test, !test.published)}
                                            disabled={working}
                                            size="small"
                                            sx={{ mr: 1 }}
                                        >
                                            {test.published ? 'Unpublish' : 'Publish'}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            onClick={async () => {
                                                if (!window.confirm('Delete this test? All mappings will be removed.')) return;
                                                try {
                                                    await axios.delete(`/api/tests/${test.id}`);
                                                    setTests(prev => prev.filter(t => t.id !== test.id));
                                                    if (selectedTest?.id === test.id) setSelectedTest(null);
                                                } catch (e) {
                                                    setError('Failed to delete test');
                                                }
                                            }}
                                        >
                                            Delete
                                        </Button>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={7}>
                    <Paper sx={{ p: 3, minHeight: 300 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">Test Details</Typography>
                            {selectedTest && (
                                <Button
                                    variant="contained"
                                    startIcon={<PlaylistAdd />}
                                    onClick={() => openAddMcqs(selectedTest)}
                                >
                                    Add MCQs
                                </Button>
                            )}
                        </Box>

                        {!selectedTest ? (
                            <Typography color="text.secondary">Select a test to manage its MCQs.</Typography>
                        ) : (
                            <>
                                <Typography variant="subtitle1" gutterBottom>
                                    {selectedTest.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    {selectedTest.description || 'No description'}
                                </Typography>
                                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                                    MCQs in this test
                                </Typography>
                                {selectedTest.mcqs && selectedTest.mcqs.length > 0 ? (
                                    <List>
                                        {selectedTest.mcqs.map((m, idx) => (
                                            <ListItem key={m.id} divider={idx < selectedTest.mcqs.length - 1}>
                                                <ListItemText
                                                    primary={m.question}
                                                    secondary={`${m.category} • ${m.difficulty}`}
                                                />
                                                <ListItemSecondaryAction>
                                                    <Button color="error" onClick={() => removeMcqFromTest(m.id)}>
                                                        Remove
                                                    </Button>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        ))}
                                    </List>
                                ) : (
                                    <Typography color="text.secondary">No MCQs added yet.</Typography>
                                )}
                            </>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Create Test Dialog */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create New Test</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Test Name"
                        value={newTest.name}
                        onChange={(e) => setNewTest(prev => ({ ...prev, name: e.target.value }))}
                        sx={{ mt: 1 }}
                    />
                    <TextField
                        fullWidth
                        label="Description (optional)"
                        value={newTest.description}
                        onChange={(e) => setNewTest(prev => ({ ...prev, description: e.target.value }))}
                        sx={{ mt: 2 }}
                        multiline
                        rows={3}
                    />
                    <TextField
                        fullWidth
                        type="number"
                        label="Duration (minutes)"
                        value={newTest.durationMinutes}
                        onChange={(e) => setNewTest(prev => ({ ...prev, durationMinutes: parseInt(e.target.value || '0', 10) }))}
                        sx={{ mt: 2 }}
                        inputProps={{ min: 1, max: 600 }}
                    />
                    <TextField
                        fullWidth
                        label="Test Instructions"
                        value={newTest.instructions}
                        onChange={(e) => setNewTest(prev => ({ ...prev, instructions: e.target.value }))}
                        sx={{ mt: 2 }}
                        multiline
                        rows={4}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={createTest} disabled={creating}>
                        {creating ? <CircularProgress size={20} /> : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add MCQs Dialog */}
            <Dialog open={addMcqsOpen} onClose={() => setAddMcqsOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Select MCQs to add</DialogTitle>
                <DialogContent>
                    <Box display="flex" gap={2} sx={{ mb: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>Filter by Category</InputLabel>
                            <Select
                                value={filterCategory}
                                label="Filter by Category"
                                onChange={(e) => setFilterCategory(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                {[...new Set(mcqs.map(m => m.category))].map(cat => (
                                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>Filter by Difficulty</InputLabel>
                            <Select
                                value={filterDifficulty}
                                label="Filter by Difficulty"
                                onChange={(e) => setFilterDifficulty(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="easy">Easy</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="hard">Hard</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <Paper variant="outlined">
                        <List>
                            {filteredMcqs.map((m, idx) => (
                                <ListItem key={m.id} divider={idx < filteredMcqs.length - 1}>
                                    <Checkbox
                                        edge="start"
                                        checked={selectedMcqIds.has(m.id)}
                                        onChange={() => handleToggleSelectMcq(m.id)}
                                        tabIndex={-1}
                                    />
                                    <ListItemText primary={m.question} secondary={`${m.category} • ${m.difficulty}`} />
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddMcqsOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={addSelectedMcqs} disabled={working}>
                        {working ? <CircularProgress size={20} /> : 'Add Selected'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default TestManager;


